import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { findings as findingsApi } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  ShieldCheck, AlertCircle, Check, Clock, ListTodo, X as XIcon,
  Store, Camera, Upload, Save, Loader2,
} from "lucide-react";
import { localizedCategory } from "../lib/i18nContent";

const STATUS_LIST = [
  { v: "to_do", icon: ListTodo, tone: "bg-stone-100 text-stone-700 ring-stone-400/20", active: "bg-stone-800 text-white" },
  { v: "in_progress", icon: Clock, tone: "bg-amber-50 text-amber-700 ring-amber-600/20", active: "bg-amber-500 text-white" },
  { v: "done", icon: Check, tone: "bg-emerald-50 text-emerald-700 ring-emerald-600/20", active: "bg-emerald-600 text-white" },
  { v: "cannot_do", icon: XIcon, tone: "bg-red-50 text-red-700 ring-red-600/20", active: "bg-red-600 text-white" },
];

const SEVERITY_COLOR = {
  Alta: "bg-red-50 text-red-700 ring-red-600/20",
  Media: "bg-amber-50 text-amber-700 ring-amber-600/20",
  Baja: "bg-stone-100 text-stone-700 ring-stone-400/20",
};

/**
 * Interactive per-item public checklist. Each row has the SAME inline editor
 * behaviour as the single-finding public page (status + notes + photo upload)
 * but rendered inside a compact card. Status changes AWAY from "to_do" must
 * include a follow-up note AND at least one photo — enforced by the backend
 * and reflected here via inline warnings.
 */
export default function PublicChecklistPage() {
  const { token } = useParams();
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  // Per-finding local edit state, keyed by finding id: { notes, author, saving, uploading, pendingStatus }
  const [rows, setRows] = useState({});
  const fileRefs = useRef({});

  const refresh = async () => {
    try {
      const r = await findingsApi.publicChecklist(token);
      setData(r);
      // Seed row state
      setRows((prev) => {
        const next = { ...prev };
        for (const f of r.findings || []) {
          if (!next[f.id]) {
            next[f.id] = {
              notes: f.follow_up_notes || "",
              author: prev[f.id]?.author || "",
              saving: false,
              uploading: false,
            };
          } else {
            // Preserve any in-progress edits, but refresh notes if user hasn't changed them
            next[f.id] = { ...next[f.id] };
          }
        }
        return next;
      });
    } catch (e) {
      setError(e?.response?.data?.detail || t("public_checklist.load_error", "No se pudo cargar el checklist."));
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const setRowField = (id, key, val) =>
    setRows((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), [key]: val } }));

  /**
   * Attempt a status change for a given finding. If the target status is
   * anything other than "to_do", we require at least one photo AND a comment.
   * The backend enforces this too but we short-circuit client-side to give a
   * clearer message.
   */
  const changeStatus = async (f, newStatus) => {
    const row = rows[f.id] || {};
    const photos = f.photos && f.photos.length > 0 ? f.photos : (f.photo_url ? [{ url: f.photo_url }] : []);
    const hasNote = ((row.notes ?? f.follow_up_notes ?? "") + "").trim().length > 0;
    const hasPhoto = photos.length > 0;
    if (newStatus !== "to_do" && (!hasNote || !hasPhoto)) {
      const missing = [];
      if (!hasPhoto) missing.push(t("public_checklist.evidence_photo", "foto"));
      if (!hasNote) missing.push(t("public_checklist.evidence_comment", "comentario"));
      toast.error(
        t("public_checklist.evidence_required", "Requiere {{items}} para cambiar el estado.", {
          items: missing.join(" + "),
        }),
      );
      return;
    }
    setRowField(f.id, "saving", true);
    try {
      await findingsApi.publicUpdate(f.share_token, {
        status: newStatus,
        follow_up_notes: row.notes ?? f.follow_up_notes ?? "",
        update_author: row.author || "",
      });
      toast.success(t("public_finding.saved", "Guardado"));
      refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (detail === "EVIDENCE_REQUIRED_COMMENT") {
        toast.error(t("public_checklist.need_comment", "Agrega un comentario antes de cambiar el estado."));
      } else if (detail === "EVIDENCE_REQUIRED_PHOTO") {
        toast.error(t("public_checklist.need_photo", "Sube al menos una foto antes de cambiar el estado."));
      } else {
        toast.error(detail || t("public_finding.save_error", "No se pudo guardar"));
      }
    } finally {
      setRowField(f.id, "saving", false);
    }
  };

  const saveNotes = async (f) => {
    const row = rows[f.id] || {};
    setRowField(f.id, "saving", true);
    try {
      await findingsApi.publicUpdate(f.share_token, {
        follow_up_notes: row.notes ?? "",
        update_author: row.author || "",
      });
      toast.success(t("public_finding.saved", "Guardado"));
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("public_finding.save_error", "No se pudo guardar"));
    } finally {
      setRowField(f.id, "saving", false);
    }
  };

  const pickFile = (id) => fileRefs.current[id]?.click();
  const uploadFile = async (f, ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    setRowField(f.id, "uploading", true);
    try {
      const row = rows[f.id] || {};
      await findingsApi.publicUpload(f.share_token, file, row.author || "");
      toast.success(t("public_finding.saved", "Foto subida"));
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("public_finding.upload_error", "No se pudo subir la foto"));
    } finally {
      setRowField(f.id, "uploading", false);
      if (fileRefs.current[f.id]) fileRefs.current[f.id].value = "";
    }
  };

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-stone-50 p-6">
        <Card className="canales-card max-w-md w-full p-8 text-center" data-testid="public-checklist-error">
          <AlertCircle className="mx-auto text-red-600" size={40} />
          <h1 className="text-lg font-semibold mt-3">{t("public_checklist.not_found", "Checklist no disponible")}</h1>
          <p className="text-sm text-stone-500 mt-2">{error}</p>
        </Card>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen grid place-items-center bg-stone-50 text-stone-500">
        {t("common.loading")}
      </div>
    );
  }

  const findings = data.findings || [];
  const doneCount = findings.filter((f) => f.status === "done").length;
  const progressPct = findings.length ? Math.round((doneCount / findings.length) * 100) : 0;

  return (
    <div className="min-h-screen bg-stone-50" data-testid="public-checklist-page">
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#0B1220] via-[#1E3A8A] to-[#2E4DAA] text-white">
        <div className="max-w-3xl mx-auto p-6 lg:p-10">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-[#F5B800] font-semibold">
            <ShieldCheck size={14} />
            {t("public_checklist.overline", "Canales · TO DO Checklist")}
          </div>
          <h1 className="font-display text-3xl md:text-4xl mt-2">
            {data.store_name || t("public_checklist.title", "Pendientes de tienda")}
          </h1>
          <p className="text-white/80 text-sm mt-2 flex items-center gap-2 flex-wrap">
            <Store size={14} className="opacity-80" />
            {t("public_checklist.count", "{{n}} pendientes", { n: findings.length })}
            {data.created_by_name && (
              <span className="text-white/60"> · {t("public_checklist.by", "Enviado por")} {data.created_by_name}</span>
            )}
          </p>
          {/* Progress bar */}
          <div className="mt-4">
            <div className="flex items-center justify-between text-[11px] text-white/70 mb-1.5">
              <span>{t("public_checklist.progress", "Progreso")}</span>
              <span>{doneCount}/{findings.length} · {progressPct}%</span>
            </div>
            <div className="h-2 rounded-full bg-white/10 overflow-hidden">
              <div
                className="h-full bg-[#F5B800] transition-all duration-500"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
          <p className="text-[11px] text-white/60 mt-4 leading-relaxed">
            💡 {t("public_checklist.rule_hint", "Al marcar cualquier estado distinto a 'To do', debes adjuntar foto y comentario. Sin evidencia, el estado no se guarda.")}
          </p>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-3xl mx-auto p-6 lg:p-10">
        {findings.length === 0 && (
          <Card className="canales-card p-8 text-center text-stone-500">
            {t("public_checklist.empty", "No hay pendientes en este checklist.")}
          </Card>
        )}

        <div className="space-y-4">
          {findings.map((f, idx) => {
            const row = rows[f.id] || {};
            const meta = STATUS_LIST.find((s) => s.v === f.status) || STATUS_LIST[0];
            const sev = SEVERITY_COLOR[f.severity] || SEVERITY_COLOR.Media;
            const photos = f.photos && f.photos.length > 0 ? f.photos : (f.photo_url ? [{ url: f.photo_url }] : []);
            const noteText = row.notes ?? f.follow_up_notes ?? "";
            const canConfirmComplete = photos.length > 0 && (noteText || "").trim().length > 0;
            return (
              <Card
                key={f.id}
                className="canales-card p-4 md:p-5"
                data-testid={`public-checklist-item-${f.id}`}
              >
                {/* Header row */}
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-md bg-[#0B1220] text-white grid place-items-center text-xs font-bold shrink-0">
                    {idx + 1}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-stone-800 text-sm">
                        {localizedCategory(f.category, i18n.language)}
                      </span>
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10.5px] font-medium ring-1 ring-inset ${sev}`}>
                        {t(`severity.${f.severity}`, f.severity)}
                      </span>
                      {f.commitment_date && (
                        <span className="text-[11px] text-stone-500">📅 {f.commitment_date}</span>
                      )}
                    </div>
                    <p className="mt-1.5 text-sm text-stone-700 leading-relaxed">{f.description}</p>
                    {/* Responsible avatar / initials */}
                    <div className="mt-2 flex items-center gap-1.5">
                      {f.responsible ? (
                        <>
                          <span
                            className="inline-flex w-6 h-6 items-center justify-center rounded-full bg-[#1E3A8A] text-white text-[10px] font-bold shrink-0"
                            title={f.responsible}
                          >
                            {(f.responsible || "?")
                              .split(/\s+/)
                              .filter(Boolean)
                              .slice(0, 2)
                              .map((w) => w[0].toUpperCase())
                              .join("")}
                          </span>
                          <span className="text-[11px] text-stone-600">
                            {t("public_checklist.responsible", "Responsable")}:{" "}
                            <span className="font-medium text-stone-800">{f.responsible}</span>
                          </span>
                        </>
                      ) : (
                        <span className="text-[11px] text-stone-400">
                          👤 {t("public_checklist.no_responsible", "Sin asignar")}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Status pills */}
                <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`item-status-picker-${f.id}`}>
                  {STATUS_LIST.map((s) => {
                    const Icon = s.icon;
                    const active = f.status === s.v;
                    // Non-to_do states need evidence; keep them clickable to
                    // reveal the friendly toast, but visually hint they're
                    // "locked" until evidence exists.
                    const locked = s.v !== "to_do" && !canConfirmComplete && !active;
                    return (
                      <button
                        key={s.v}
                        onClick={() => changeStatus(f, s.v)}
                        disabled={!!row.saving}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[12px] font-semibold transition-all ${
                          active
                            ? s.active
                            : locked
                            ? "bg-white border border-dashed border-stone-300 text-stone-400"
                            : "bg-white border border-stone-200 text-stone-700 hover:border-stone-400"
                        }`}
                        data-testid={`item-status-${f.id}-${s.v}`}
                        title={locked ? t("public_checklist.locked_hint", "Sube foto y comentario para desbloquear") : ""}
                      >
                        <Icon size={12} />
                        {t(`finding_status.${s.v}`)}
                        {locked && <span className="ml-0.5 text-[10px]">🔒</span>}
                      </button>
                    );
                  })}
                </div>

                {/* Evidence guard status */}
                {!canConfirmComplete && (
                  <div className="mt-2 text-[11px] text-stone-500 flex items-center gap-1.5">
                    <AlertCircle size={12} className="text-amber-500" />
                    {t("public_checklist.evidence_missing", "Falta {{items}} para poder marcar en curso / completo / no puedo.", {
                      items: [
                        photos.length === 0 ? t("public_checklist.evidence_photo", "foto") : null,
                        (noteText || "").trim().length === 0 ? t("public_checklist.evidence_comment", "comentario") : null,
                      ].filter(Boolean).join(" + "),
                    })}
                  </div>
                )}

                {/* Photos */}
                {photos.length > 0 && (
                  <div className="mt-3 flex gap-1.5 flex-wrap" data-testid={`item-photos-${f.id}`}>
                    {photos.map((p, i) => (
                      <a
                        key={i}
                        href={p.url}
                        target="_blank"
                        rel="noreferrer"
                        className="w-16 h-16 rounded-md border border-stone-200 overflow-hidden hover:scale-105 transition-transform"
                      >
                        <img src={p.url} alt="evidencia" className="w-full h-full object-cover" />
                      </a>
                    ))}
                  </div>
                )}

                {/* Comment textarea */}
                <div className="mt-3">
                  <label className="text-[11px] uppercase tracking-widest font-semibold text-stone-500">
                    {t("public_finding.notes", "Comentario / seguimiento")}
                  </label>
                  <Textarea
                    value={noteText}
                    onChange={(e) => setRowField(f.id, "notes", e.target.value)}
                    placeholder={t("public_finding.notes_placeholder", "Explica qué se hizo, obstáculos, próximo paso…")}
                    className="mt-1 min-h-[70px] text-sm"
                    data-testid={`item-notes-${f.id}`}
                  />
                </div>

                {/* Actions row */}
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <input
                    ref={(el) => (fileRefs.current[f.id] = el)}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => uploadFile(f, e)}
                    className="hidden"
                    data-testid={`item-file-input-${f.id}`}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => pickFile(f.id)}
                    disabled={row.uploading}
                    className="gap-1.5"
                    data-testid={`item-upload-btn-${f.id}`}
                  >
                    {row.uploading ? (
                      <Loader2 size={13} className="animate-spin" />
                    ) : photos.length > 0 ? (
                      <Camera size={13} />
                    ) : (
                      <Upload size={13} />
                    )}
                    {photos.length > 0
                      ? t("public_finding.add_more_photo", "Agregar otra foto")
                      : t("public_finding.upload_photo", "Subir foto")}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => saveNotes(f)}
                    disabled={row.saving}
                    className="gap-1.5"
                    data-testid={`item-save-notes-${f.id}`}
                  >
                    {row.saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                    {t("public_finding.save_notes", "Guardar comentario")}
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>

        <p className="text-[11px] text-stone-400 text-center mt-8">
          © Mueblería Canales · Operations Audit
        </p>
      </div>
    </div>
  );
}
